import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'

/* Utils */
import {
    useTheme,
    Text,
    Grid,
    GridItem,
    Box,
    Stack,
    Button
} from '@salesforce/retail-react-app/app/components/shared/ui'
import { FormattedMessage, useIntl } from 'react-intl';

import {useDerivedProduct} from '@salesforce/retail-react-app/app/hooks'

/* Components */
import StarRating from '../../components/pdp-reviews/partials/star-rating';
import FitSelector from '../../components/pdp-reviews/partials/fit-selector';
import Field from '../../components/field'

/* Fields */
import useWriteReviewFields from '../../components/forms/useWriteReviewFields'
import ImageUploader from  '../pdp-reviews/partials/image-uploader'
import { color } from '@chakra-ui/react';

const WriteReviewFields = ({
    reviewFormData,
    form,
    prefix = '',
    children,
    product
}) => {
    const theme = useTheme()
    const {formatMessage} = useIntl()
    const [rating, setRating] = useState(0)
    const [fit, setFit] = useState([3])
    const [selectedSize, setSelectedSize] = useState('');
    const [selectedHeight, setSelectedHeight] = useState('');
    const [selectedWeight, setSelectedWeight] = useState('');
    const fields = useWriteReviewFields({reviewFormData, form, prefix})

    const getSelectFieldStyling = ({ selectedValue, paddingLeft = '16px', color = 'black' }) => ({
        sx: {
            color: selectedValue ? 'black' : 'gray.600',
            'option': {
                color: color,
                'padding-left': paddingLeft
            },
            'option[value=""]': {
                color: 'gray.600'
            },
            'padding-inline-start': 0
        },
        fontSize: '14px',
        height: {base: '40px', lg: '34px'},
        lineHeight: '18px'
    });

    // Set the static value for the hidden input when the component mounts
    useEffect(() => {
        form.setValue(reviewFormData?.agreedtotermsandconditions?.Id, true);
    }, [form, reviewFormData]);

    const handleRatingChange = (newRating) => {
        setRating(newRating)
        form.setValue(reviewFormData?.rating?.Id, newRating);
        if (newRating > 0) {
            form.clearErrors(reviewFormData?.rating?.Id);
        }
    }

    const handleOnFitChange = (newFit) => {
        setFit(newFit[0]);
        form.setValue(reviewFormData?.rating_HowDoesItFit?.Id, newFit[0]);
    }

    const handleOnHeightChange = (e) => {
        const newHeight = e.target.value;
        setSelectedHeight(newHeight);
        form.setValue(reviewFormData?.contextdatavalue_height?.Id, newHeight);
    }

    const handleOnWeightChange = (e) => {
        const newWeight = e.target.value;
        setSelectedWeight(newWeight);
        form.setValue(reviewFormData?.contextdatavalue_weight?.Id, newWeight);
    }

    const handleOnTitleChange = (e) => {
        form.setValue(reviewFormData?.title?.Id, e.target.value);
    };

    const handleOnTextChange = (e) => {
        form.setValue(reviewFormData?.reviewtext?.Id, e.target.value);
    }

    const handleOnNicknameChange = (e) => {
        form.setValue(reviewFormData?.usernickname?.Id, e.target.value);
    }

    const handleOnEmailChange = (e) => {
        form.setValue(reviewFormData?.useremail?.Id, e.target.value);
    }

    const handleOnSizeChange = (e) => {
        const newSize = e.target.value;
        setSelectedSize(newSize);
        form.setValue(reviewFormData?.additionalfield_sizeOrdered?.Id, newSize);
    }

    const { variationAttributes } = useDerivedProduct(product)
    const sizeAttribute = variationAttributes.find(attr => attr.id === 'size');
    const sizeOptions = sizeAttribute ? sizeAttribute.values.map(option => ({
        label: option.name,
        value: option.value
    })) : [];

    // Preselect the size if there is only one option
    useEffect(() => {
        if (sizeOptions.length === 1) {
            const singleOption = sizeOptions[0];
            form.setValue(reviewFormData?.additionalfield_sizeOrdered?.Id, singleOption.value);
        }
    }, [sizeOptions, form, reviewFormData]);

    const transformedHeightOptions = reviewFormData?.contextdatavalue_height.Options
        .filter(option => option.Label !== '' && option.Value !== '') // Filter out empty options
        .map(option => ({
            label: option.Label,
            value: option.Value
        }));

    const transformedWeightOptions = reviewFormData?.contextdatavalue_weight.Options
        .filter(option => option.Label !== '' && option.Value !== '') // Filter out empty options
        .map(option => ({
            label: option.Label,
            value: option.Value
        }));

    return (
        <Box>
            <Grid templateColumns={{base: '1fr auto'}}
                gap={{base: '75px'}}
                mt={{base: 8}}>
                <GridItem colSpan={1}>
                    {/* Overall rating */}
                    <Text {...theme.components.WriteReview.fieldTitle}>
                        <FormattedMessage
                            defaultMessage="Overall Rating *"
                            id="pdp_reviews.write.overall"
                        />
                    </Text>
                    <StarRating key={'write-review-rating'}
                        styles={{starWidth: '24', starHeight: '24'}}
                        onRatingChange={handleRatingChange}
                    />

                    {/* I use standard input because the rating is not a standard input */}
                    <input
                        type="hidden"
                        {...form.register(reviewFormData?.rating?.Id, {
                            validate: (value) => value > 0 || formatMessage({
                                defaultMessage: 'You must enter a value for overall rating.',
                                id: 'pdp_reviews.required.rating'
                            })
                        })}
                        value={rating}
                    />
                    {form.formState.errors[reviewFormData?.rating?.Id] && (
                        <Text color="red.600"
                            mt={2}
                            fontSize={'sm'}>
                            {form.formState.errors[reviewFormData?.rating?.Id].message}
                        </Text>
                    )}

                    {/* Size ordered */}
                    <Field
                        labelProps={{...theme.components.WriteReview.fieldTitle, mt: 6}}
                        {...fields?.additionalfield_sizeOrdered}
                        options={sizeOptions}
                        inputProps={{
                            ...getSelectFieldStyling({ selectedValue: selectedSize }),
                            onChange: handleOnSizeChange,
                            _focusVisible: {
                                boxShadow: 'var(--chakra-shadows-outline)'
                            }
                        }}
                        isDisabled={sizeOptions.length === 1} // Disable if only one option
                    />
                </GridItem>
                <GridItem colSpan={1}>
                    {children}
                </GridItem>
            </Grid>

            {/* Fit selector */}
            <FitSelector onFitChange={handleOnFitChange} options={reviewFormData?.rating_HowDoesItFit?.Options} />
            {/* I use standard input because the fit is not a standard input */}
            <input
                type="hidden"
                {...form.register(reviewFormData?.rating_HowDoesItFit?.Id)}
                value={fit}
            />

            <Text {...theme.components.WriteReview.fieldTitle} mt={8}>
                <FormattedMessage
                    defaultMessage="Share your Review"
                    id="pdp_reviews.share.review"
                />
            </Text>

            <Stack spacing={4}>
                <Field
                    labelProps={{...theme.components.WriteReview.fieldTitle}}
                    {...fields?.title}
                    inputProps={{
                        onBlur: handleOnTitleChange
                    }}
                />

                <Field
                    labelProps={{...theme.components.WriteReview.fieldTitle}}
                    {...fields?.reviewtext}
                    inputProps={{
                        onBlur: handleOnTextChange
                    }}
                />
            </Stack>

            <Text {...theme.components.WriteReview.fieldTitle} mt={8} >
                <FormattedMessage
                    defaultMessage="Add Photos"
                    id="pdp_reviews.add.photos"
                />
            </Text>

            <ImageUploader reviewFormData={reviewFormData} form={form} />

            <Text fontSize={{base: 'xs'}} mt={{base: 2, lg: '14px'}}>
                <FormattedMessage
                    defaultMessage="Add up to 6 photos. Maximum of 10MB per photo."
                    id="pdp_reviews.add.photos.description"
                />
            </Text>

            <Text {...theme.components.WriteReview.fieldTitle} mt={8} >
                <FormattedMessage
                    defaultMessage="Your Information"
                    id="pdp_reviews.your.information"
                />
            </Text>

            <Stack spacing={4}>
                <Field
                    labelProps={{...theme.components.WriteReview.fieldTitle}}
                    {...fields?.usernickname}
                    inputProps={{
                        onBlur: handleOnNicknameChange
                    }}
                />

                <Field
                    labelProps={{...theme.components.WriteReview.fieldTitle}}
                    {...fields?.useremail}
                    inputProps={{
                        onBlur: handleOnEmailChange
                    }}
                />
            </Stack>

            <Grid templateColumns={{base: '1fr 1fr'}}
                gap={{base: '9px'}}
                mt={{base: 4}}>
                <GridItem colSpan={1}>
                    <Field
                        labelProps={{...theme.components.WriteReview.fieldTitle}}
                        {...fields?.contextdatavalue_height}
                        options={transformedHeightOptions}
                        inputProps={{
                            ...getSelectFieldStyling({ selectedValue: selectedHeight }),
                            onChange: handleOnHeightChange,
                            _focusVisible: {
                                boxShadow: 'var(--chakra-shadows-outline)'
                            }
                        }}
                    />
                </GridItem>
                <GridItem colSpan={1}>
                    <Field
                        labelProps={{...theme.components.WriteReview.fieldTitle}}
                        {...fields?.contextdatavalue_weight}
                        options={transformedWeightOptions}
                        inputProps={{
                            ...getSelectFieldStyling({ selectedValue: selectedWeight }),
                            onChange: handleOnWeightChange,
                            _focusVisible: {
                                boxShadow: 'var(--chakra-shadows-outline)'
                            }
                        }}
                    />
                </GridItem>
            </Grid>

            <input
                type="hidden"
                {...form.register(reviewFormData?.agreedtotermsandconditions?.Id)}
            />
        </Box>
    )
}

WriteReviewFields.propTypes = {
    reviewFormData: PropTypes.object.isRequired,
    form: PropTypes.object,
    prefix: PropTypes.string,
    children: PropTypes.node
}

export default WriteReviewFields
